import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import { normalizeLanguageCode, isSupportedLanguage } from '@/lib/languageUtils';
// One declaration of the URL prefixes, shared with the router, the SSG route
// filter and the build scripts. See src/config/langPrefixes.ts.
import { urlLangOf } from '@/config/langPrefixes';

// Only English is statically imported — always available without async loading.
// All other languages are lazy-loaded on demand via dynamic import().
import enTranslation from '../locales/en/translation.json';
import enCommon from '../locales/en/common.json';
import enDashboard from '../locales/en/dashboard.json';
import enCCTVDesigner from '../locales/en/cctv-designer.json';
import enValidation from '../locales/en/validation.json';
import enCalculators from '../locales/en/calculators.json';
import enRodo from '../locales/en/rodo.json';
import enTerms from '../locales/en/terms.json';
import enLens from '../locales/en/lens.json';
import enTenders from '../locales/en/tenders.json';

// Lazy loaders for all non-English languages (code-split per language)
const languageLoaders: Record<string, Record<string, () => Promise<any>>> = {
  pl: {
    translation: () => import('../locales/pl/translation.json'),
    common: () => import('../locales/pl/common.json'),
    dashboard: () => import('../locales/pl/dashboard.json'),
    'cctv-designer': () => import('../locales/pl/cctv-designer.json'),
    validation: () => import('../locales/pl/validation.json'),
    calculators: () => import('../locales/pl/calculators.json'),
    rodo: () => import('../locales/pl/rodo.json'),
    terms: () => import('../locales/pl/terms.json'),
    lens: () => import('../locales/pl/lens.json'),
    tenders: () => import('../locales/pl/tenders.json'),
  },
  de: {
    translation: () => import('../locales/de/translation.json'),
    common: () => import('../locales/de/common.json'),
    dashboard: () => import('../locales/de/dashboard.json'),
    'cctv-designer': () => import('../locales/de/cctv-designer.json'),
    validation: () => import('../locales/de/validation.json'),
    calculators: () => import('../locales/de/calculators.json'),
    rodo: () => import('../locales/de/rodo.json'),
    terms: () => import('../locales/de/terms.json'),
    lens: () => import('../locales/de/lens.json'),
    tenders: () => import('../locales/de/tenders.json'),
  },
  'pt-br': {
    translation: () => import('../locales/pt-br/translation.json'),
    common: () => import('../locales/pt-br/common.json'),
    dashboard: () => import('../locales/pt-br/dashboard.json'),
    'cctv-designer': () => import('../locales/pt-br/cctv-designer.json'),
    validation: () => import('../locales/pt-br/validation.json'),
    calculators: () => import('../locales/pt-br/calculators.json'),
    rodo: () => import('../locales/pt-br/rodo.json'),
    terms: () => import('../locales/pt-br/terms.json'),
    lens: () => import('../locales/pt-br/lens.json'),
    tenders: () => import('../locales/pt-br/tenders.json'),
  },
  'pt-pt': {
    translation: () => import('../locales/pt-pt/translation.json'),
    common: () => import('../locales/pt-pt/common.json'),
    dashboard: () => import('../locales/pt-pt/dashboard.json'),
    'cctv-designer': () => import('../locales/pt-pt/cctv-designer.json'),
    validation: () => import('../locales/pt-pt/validation.json'),
    calculators: () => import('../locales/pt-pt/calculators.json'),
    rodo: () => import('../locales/pt-pt/rodo.json'),
    terms: () => import('../locales/pt-pt/terms.json'),
    lens: () => import('../locales/pt-pt/lens.json'),
    tenders: () => import('../locales/pt-pt/tenders.json'),
  },
  es: {
    translation: () => import('../locales/es/translation.json'),
    common: () => import('../locales/es/common.json'),
    dashboard: () => import('../locales/es/dashboard.json'),
    'cctv-designer': () => import('../locales/es/cctv-designer.json'),
    validation: () => import('../locales/es/validation.json'),
    calculators: () => import('../locales/es/calculators.json'),
    rodo: () => import('../locales/es/rodo.json'),
    terms: () => import('../locales/es/terms.json'),
    lens: () => import('../locales/es/lens.json'),
    tenders: () => import('../locales/es/tenders.json'),
  },
  hi: {
    translation: () => import('../locales/hi/translation.json'),
    common: () => import('../locales/hi/common.json'),
    dashboard: () => import('../locales/hi/dashboard.json'),
    'cctv-designer': () => import('../locales/hi/cctv-designer.json'),
    validation: () => import('../locales/hi/validation.json'),
    calculators: () => import('../locales/hi/calculators.json'),
    rodo: () => import('../locales/hi/rodo.json'),
    terms: () => import('../locales/hi/terms.json'),
    lens: () => import('../locales/hi/lens.json'),
    tenders: () => import('../locales/hi/tenders.json'),
  },
  id: {
    translation: () => import('../locales/id/translation.json'),
    common: () => import('../locales/id/common.json'),
    dashboard: () => import('../locales/id/dashboard.json'),
    'cctv-designer': () => import('../locales/id/cctv-designer.json'),
    validation: () => import('../locales/id/validation.json'),
    calculators: () => import('../locales/id/calculators.json'),
    rodo: () => import('../locales/id/rodo.json'),
    terms: () => import('../locales/id/terms.json'),
    lens: () => import('../locales/id/lens.json'),
    tenders: () => import('../locales/id/tenders.json'),
  },
  fil: {
    translation: () => import('../locales/fil/translation.json'),
    common: () => import('../locales/fil/common.json'),
    dashboard: () => import('../locales/fil/dashboard.json'),
    'cctv-designer': () => import('../locales/fil/cctv-designer.json'),
    validation: () => import('../locales/fil/validation.json'),
    calculators: () => import('../locales/fil/calculators.json'),
    rodo: () => import('../locales/fil/rodo.json'),
    terms: () => import('../locales/fil/terms.json'),
    lens: () => import('../locales/fil/lens.json'),
    tenders: () => import('../locales/fil/tenders.json'),
  },
  tr: {
    translation: () => import('../locales/tr/translation.json'),
    common: () => import('../locales/tr/common.json'),
    dashboard: () => import('../locales/tr/dashboard.json'),
    'cctv-designer': () => import('../locales/tr/cctv-designer.json'),
    validation: () => import('../locales/tr/validation.json'),
    calculators: () => import('../locales/tr/calculators.json'),
    rodo: () => import('../locales/tr/rodo.json'),
    terms: () => import('../locales/tr/terms.json'),
    lens: () => import('../locales/tr/lens.json'),
    tenders: () => import('../locales/tr/tenders.json'),
  },
  ar: {
    translation: () => import('../locales/ar/translation.json'),
    common: () => import('../locales/ar/common.json'),
    dashboard: () => import('../locales/ar/dashboard.json'),
    'cctv-designer': () => import('../locales/ar/cctv-designer.json'),
    validation: () => import('../locales/ar/validation.json'),
    calculators: () => import('../locales/ar/calculators.json'),
    rodo: () => import('../locales/ar/rodo.json'),
    terms: () => import('../locales/ar/terms.json'),
    lens: () => import('../locales/ar/lens.json'),
    tenders: () => import('../locales/ar/tenders.json'),
  },
  vi: {
    translation: () => import('../locales/vi/translation.json'),
    common: () => import('../locales/vi/common.json'),
    dashboard: () => import('../locales/vi/dashboard.json'),
    'cctv-designer': () => import('../locales/vi/cctv-designer.json'),
    validation: () => import('../locales/vi/validation.json'),
    calculators: () => import('../locales/vi/calculators.json'),
    rodo: () => import('../locales/vi/rodo.json'),
    terms: () => import('../locales/vi/terms.json'),
    lens: () => import('../locales/vi/lens.json'),
    tenders: () => import('../locales/vi/tenders.json'),
  },
  th: {
    translation: () => import('../locales/th/translation.json'),
    common: () => import('../locales/th/common.json'),
    dashboard: () => import('../locales/th/dashboard.json'),
    'cctv-designer': () => import('../locales/th/cctv-designer.json'),
    validation: () => import('../locales/th/validation.json'),
    calculators: () => import('../locales/th/calculators.json'),
    rodo: () => import('../locales/th/rodo.json'),
    terms: () => import('../locales/th/terms.json'),
    lens: () => import('../locales/th/lens.json'),
    tenders: () => import('../locales/th/tenders.json'),
  },
  ja: {
    translation: () => import('../locales/ja/translation.json'),
    common: () => import('../locales/ja/common.json'),
    dashboard: () => import('../locales/ja/dashboard.json'),
    'cctv-designer': () => import('../locales/ja/cctv-designer.json'),
    validation: () => import('../locales/ja/validation.json'),
    calculators: () => import('../locales/ja/calculators.json'),
    rodo: () => import('../locales/ja/rodo.json'),
    terms: () => import('../locales/ja/terms.json'),
    lens: () => import('../locales/ja/lens.json'),
    tenders: () => import('../locales/ja/tenders.json'),
  },
  ko: {
    translation: () => import('../locales/ko/translation.json'),
    common: () => import('../locales/ko/common.json'),
    dashboard: () => import('../locales/ko/dashboard.json'),
    'cctv-designer': () => import('../locales/ko/cctv-designer.json'),
    validation: () => import('../locales/ko/validation.json'),
    calculators: () => import('../locales/ko/calculators.json'),
    rodo: () => import('../locales/ko/rodo.json'),
    terms: () => import('../locales/ko/terms.json'),
    lens: () => import('../locales/ko/lens.json'),
    tenders: () => import('../locales/ko/tenders.json'),
  },
  fr: {
    translation: () => import('../locales/fr/translation.json'),
    common: () => import('../locales/fr/common.json'),
    dashboard: () => import('../locales/fr/dashboard.json'),
    'cctv-designer': () => import('../locales/fr/cctv-designer.json'),
    validation: () => import('../locales/fr/validation.json'),
    calculators: () => import('../locales/fr/calculators.json'),
    rodo: () => import('../locales/fr/rodo.json'),
    terms: () => import('../locales/fr/terms.json'),
    lens: () => import('../locales/fr/lens.json'),
    tenders: () => import('../locales/fr/tenders.json'),
  },
  it: {
    translation: () => import('../locales/it/translation.json'),
    common: () => import('../locales/it/common.json'),
    dashboard: () => import('../locales/it/dashboard.json'),
    'cctv-designer': () => import('../locales/it/cctv-designer.json'),
    validation: () => import('../locales/it/validation.json'),
    calculators: () => import('../locales/it/calculators.json'),
    rodo: () => import('../locales/it/rodo.json'),
    terms: () => import('../locales/it/terms.json'),
    lens: () => import('../locales/it/lens.json'),
    tenders: () => import('../locales/it/tenders.json'),
  },
  uk: {
    translation: () => import('../locales/uk/translation.json'),
    common: () => import('../locales/uk/common.json'),
    dashboard: () => import('../locales/uk/dashboard.json'),
    'cctv-designer': () => import('../locales/uk/cctv-designer.json'),
    validation: () => import('../locales/uk/validation.json'),
    calculators: () => import('../locales/uk/calculators.json'),
    rodo: () => import('../locales/uk/rodo.json'),
    terms: () => import('../locales/uk/terms.json'),
    lens: () => import('../locales/uk/lens.json'),
    tenders: () => import('../locales/uk/tenders.json'),
  },
  cs: {
    translation: () => import('../locales/cs/translation.json'),
    common: () => import('../locales/cs/common.json'),
    dashboard: () => import('../locales/cs/dashboard.json'),
    'cctv-designer': () => import('../locales/cs/cctv-designer.json'),
    validation: () => import('../locales/cs/validation.json'),
    calculators: () => import('../locales/cs/calculators.json'),
    rodo: () => import('../locales/cs/rodo.json'),
    terms: () => import('../locales/cs/terms.json'),
    lens: () => import('../locales/cs/lens.json'),
    tenders: () => import('../locales/cs/tenders.json'),
  },
  nl: {
    translation: () => import('../locales/nl/translation.json'),
    common: () => import('../locales/nl/common.json'),
    dashboard: () => import('../locales/nl/dashboard.json'),
    'cctv-designer': () => import('../locales/nl/cctv-designer.json'),
    validation: () => import('../locales/nl/validation.json'),
    calculators: () => import('../locales/nl/calculators.json'),
    rodo: () => import('../locales/nl/rodo.json'),
    terms: () => import('../locales/nl/terms.json'),
    lens: () => import('../locales/nl/lens.json'),
    tenders: () => import('../locales/nl/tenders.json'),
  },
  hu: {
    translation: () => import('../locales/hu/translation.json'),
    common: () => import('../locales/hu/common.json'),
    dashboard: () => import('../locales/hu/dashboard.json'),
    'cctv-designer': () => import('../locales/hu/cctv-designer.json'),
    validation: () => import('../locales/hu/validation.json'),
    calculators: () => import('../locales/hu/calculators.json'),
    rodo: () => import('../locales/hu/rodo.json'),
    terms: () => import('../locales/hu/terms.json'),
    lens: () => import('../locales/hu/lens.json'),
    tenders: () => import('../locales/hu/tenders.json'),
  },
  lt: {
    translation: () => import('../locales/lt/translation.json'),
    common: () => import('../locales/lt/common.json'),
    dashboard: () => import('../locales/lt/dashboard.json'),
    'cctv-designer': () => import('../locales/lt/cctv-designer.json'),
    validation: () => import('../locales/lt/validation.json'),
    calculators: () => import('../locales/lt/calculators.json'),
    rodo: () => import('../locales/lt/rodo.json'),
    terms: () => import('../locales/lt/terms.json'),
    lens: () => import('../locales/lt/lens.json'),
    tenders: () => import('../locales/lt/tenders.json'),
  },
  ro: {
    translation: () => import('../locales/ro/translation.json'),
    common: () => import('../locales/ro/common.json'),
    dashboard: () => import('../locales/ro/dashboard.json'),
    'cctv-designer': () => import('../locales/ro/cctv-designer.json'),
    validation: () => import('../locales/ro/validation.json'),
    calculators: () => import('../locales/ro/calculators.json'),
    rodo: () => import('../locales/ro/rodo.json'),
    terms: () => import('../locales/ro/terms.json'),
    lens: () => import('../locales/ro/lens.json'),
    tenders: () => import('../locales/ro/tenders.json'),
  },
  sv: {
    translation: () => import('../locales/sv/translation.json'),
    common: () => import('../locales/sv/common.json'),
    dashboard: () => import('../locales/sv/dashboard.json'),
    'cctv-designer': () => import('../locales/sv/cctv-designer.json'),
    validation: () => import('../locales/sv/validation.json'),
    calculators: () => import('../locales/sv/calculators.json'),
    rodo: () => import('../locales/sv/rodo.json'),
    terms: () => import('../locales/sv/terms.json'),
    lens: () => import('../locales/sv/lens.json'),
    tenders: () => import('../locales/sv/tenders.json'),
  },
  bg: {
    translation: () => import('../locales/bg/translation.json'),
    common: () => import('../locales/bg/common.json'),
    dashboard: () => import('../locales/bg/dashboard.json'),
    'cctv-designer': () => import('../locales/bg/cctv-designer.json'),
    validation: () => import('../locales/bg/validation.json'),
    calculators: () => import('../locales/bg/calculators.json'),
    rodo: () => import('../locales/bg/rodo.json'),
    terms: () => import('../locales/bg/terms.json'),
    lens: () => import('../locales/bg/lens.json'),
    tenders: () => import('../locales/bg/tenders.json'),
  },
  da: {
    translation: () => import('../locales/da/translation.json'),
    common: () => import('../locales/da/common.json'),
    dashboard: () => import('../locales/da/dashboard.json'),
    'cctv-designer': () => import('../locales/da/cctv-designer.json'),
    validation: () => import('../locales/da/validation.json'),
    calculators: () => import('../locales/da/calculators.json'),
    rodo: () => import('../locales/da/rodo.json'),
    terms: () => import('../locales/da/terms.json'),
    lens: () => import('../locales/da/lens.json'),
    tenders: () => import('../locales/da/tenders.json'),
  },
  el: {
    translation: () => import('../locales/el/translation.json'),
    common: () => import('../locales/el/common.json'),
    dashboard: () => import('../locales/el/dashboard.json'),
    'cctv-designer': () => import('../locales/el/cctv-designer.json'),
    validation: () => import('../locales/el/validation.json'),
    calculators: () => import('../locales/el/calculators.json'),
    rodo: () => import('../locales/el/rodo.json'),
    terms: () => import('../locales/el/terms.json'),
    lens: () => import('../locales/el/lens.json'),
    tenders: () => import('../locales/el/tenders.json'),
  },
  et: {
    translation: () => import('../locales/et/translation.json'),
    common: () => import('../locales/et/common.json'),
    dashboard: () => import('../locales/et/dashboard.json'),
    'cctv-designer': () => import('../locales/et/cctv-designer.json'),
    validation: () => import('../locales/et/validation.json'),
    calculators: () => import('../locales/et/calculators.json'),
    rodo: () => import('../locales/et/rodo.json'),
    terms: () => import('../locales/et/terms.json'),
    lens: () => import('../locales/et/lens.json'),
    tenders: () => import('../locales/et/tenders.json'),
  },
  fi: {
    translation: () => import('../locales/fi/translation.json'),
    common: () => import('../locales/fi/common.json'),
    dashboard: () => import('../locales/fi/dashboard.json'),
    'cctv-designer': () => import('../locales/fi/cctv-designer.json'),
    validation: () => import('../locales/fi/validation.json'),
    calculators: () => import('../locales/fi/calculators.json'),
    rodo: () => import('../locales/fi/rodo.json'),
    terms: () => import('../locales/fi/terms.json'),
    lens: () => import('../locales/fi/lens.json'),
    tenders: () => import('../locales/fi/tenders.json'),
  },
  hr: {
    translation: () => import('../locales/hr/translation.json'),
    common: () => import('../locales/hr/common.json'),
    dashboard: () => import('../locales/hr/dashboard.json'),
    'cctv-designer': () => import('../locales/hr/cctv-designer.json'),
    validation: () => import('../locales/hr/validation.json'),
    calculators: () => import('../locales/hr/calculators.json'),
    rodo: () => import('../locales/hr/rodo.json'),
    terms: () => import('../locales/hr/terms.json'),
    lens: () => import('../locales/hr/lens.json'),
    tenders: () => import('../locales/hr/tenders.json'),
  },
  lv: {
    translation: () => import('../locales/lv/translation.json'),
    common: () => import('../locales/lv/common.json'),
    dashboard: () => import('../locales/lv/dashboard.json'),
    'cctv-designer': () => import('../locales/lv/cctv-designer.json'),
    validation: () => import('../locales/lv/validation.json'),
    calculators: () => import('../locales/lv/calculators.json'),
    rodo: () => import('../locales/lv/rodo.json'),
    terms: () => import('../locales/lv/terms.json'),
    lens: () => import('../locales/lv/lens.json'),
    tenders: () => import('../locales/lv/tenders.json'),
  },
  sk: {
    translation: () => import('../locales/sk/translation.json'),
    common: () => import('../locales/sk/common.json'),
    dashboard: () => import('../locales/sk/dashboard.json'),
    'cctv-designer': () => import('../locales/sk/cctv-designer.json'),
    validation: () => import('../locales/sk/validation.json'),
    calculators: () => import('../locales/sk/calculators.json'),
    rodo: () => import('../locales/sk/rodo.json'),
    terms: () => import('../locales/sk/terms.json'),
    lens: () => import('../locales/sk/lens.json'),
    tenders: () => import('../locales/sk/tenders.json'),
  },
  sl: {
    translation: () => import('../locales/sl/translation.json'),
    common: () => import('../locales/sl/common.json'),
    dashboard: () => import('../locales/sl/dashboard.json'),
    'cctv-designer': () => import('../locales/sl/cctv-designer.json'),
    validation: () => import('../locales/sl/validation.json'),
    calculators: () => import('../locales/sl/calculators.json'),
    rodo: () => import('../locales/sl/rodo.json'),
    terms: () => import('../locales/sl/terms.json'),
    lens: () => import('../locales/sl/lens.json'),
    tenders: () => import('../locales/sl/tenders.json'),
  },
};

const enResources = {
  en: {
    translation: enTranslation,
    common: enCommon,
    dashboard: enDashboard,
    'cctv-designer': enCCTVDesigner,
    validation: enValidation,
    calculators: enCalculators,
    rodo: enRodo,    terms: enTerms,
    lens: enLens,
    tenders: enTenders,
  },
};

// Detect language from the current URL path (for hydration matching)
function getUrlLanguage(): string {
  if (typeof window === 'undefined') return 'en';
  return urlLangOf(window.location.pathname);
}

// Get saved language from localStorage or default to English
const getSavedLanguage = (): string => {
  if (import.meta.env.SSR) return 'en';
  try {
    const saved = localStorage.getItem('i18n_language');
    if (saved) {
      const normalized = normalizeLanguageCode(saved);
      if (normalized && isSupportedLanguage(normalized)) {
        return normalized;
      }
    }
  } catch (e) {
    console.error('Error reading language preference:', e);
  }
  return 'en';
};

// Initialize i18next with English only. Other languages are loaded on demand.
// During SSG and client hydration, the correct language is loaded before rendering
// via the async callback in main.tsx.
const initLang = import.meta.env.SSR ? 'en' : getUrlLanguage();

i18n
  .use(initReactI18next)
  .init({
    resources: enResources,
    fallbackLng: 'en',
    defaultNS: 'translation',
    ns: ['translation', 'common', 'dashboard', 'cctv-designer', 'validation', 'calculators', 'rodo', 'terms', 'lens', 'tenders'],
    lng: initLang,
    interpolation: {
      escapeValue: false,
    },
    lowerCaseLng: true,
    supportedLngs: ['en', 'pl', 'de', 'pt-br', 'pt-pt', 'es', 'hi', 'id', 'fil', 'tr', 'ar', 'vi', 'th', 'ja', 'ko', 'fr', 'it', 'uk', 'cs', 'nl', 'hu', 'lt', 'ro', 'sv', 'bg', 'da', 'el', 'et', 'fi', 'hr', 'lv', 'sk', 'sl'],
    fallbackNS: 'translation',
  });

/**
 * Load a language on demand and return the namespaces that failed to load.
 * Every namespace that did load is registered: with Promise.all one dropped
 * chunk threw away the other nine, and the page hydrated in English under its
 * locale URL (reproduced on prod 2026-09-26 by blocking only `lens-*.js` on
 * /fr/calculator/fov; Google then filed the locale pages as duplicates of EN).
 * A failed module is not retried here: the module map caches the failure.
 * `reasons`, when given, receives each failed namespace's error message.
 */
export const loadLanguageReport = async (
  lang: string,
  reasons?: Record<string, string>,
): Promise<string[]> => {
  if (lang === 'en') return []; // English is always available

  const loaders = languageLoaders[lang];
  if (!loaders) return [];

  // Find namespaces that aren't loaded yet
  const missing = Object.entries(loaders).filter(
    ([ns]) => !i18n.hasResourceBundle(lang, ns)
  );

  if (missing.length === 0) return [];

  const results = await Promise.allSettled(
    missing.map(async ([ns, loader]) => {
      const module = await loader();
      return [ns, module.default || module] as const;
    })
  );
  const failed: string[] = [];
  results.forEach((result, i) => {
    if (result.status === 'fulfilled') {
      const [ns, data] = result.value;
      i18n.addResourceBundle(lang, ns, data, true, true);
    } else {
      failed.push(missing[i][0]);
      if (reasons) {
        const reason = result.reason as { message?: unknown } | undefined;
        reasons[missing[i][0]] = String(reason?.message ?? result.reason).slice(0, 300);
      }
      console.error(`Error loading language ${lang}/${missing[i][0]}:`, result.reason);
    }
  });
  return failed;
};

// The signed-in app's namespace. No prerendered page renders it for a visitor,
// so losing it cannot put English over the server's translated page.
const APP_ONLY_NAMESPACES = new Set(['dashboard']);

/** The failed namespaces that would put English over a prerendered page. */
export const namespacesBlockingHydration = (failed: readonly string[]): string[] =>
  failed.filter((ns) => !APP_ONLY_NAMESPACES.has(ns));

// Load a language on demand — fetches all missing namespaces via dynamic import
export const loadLanguage = async (lang: string): Promise<void> => {
  await loadLanguageReport(lang);
};

// Call this AFTER hydration (e.g. in a useEffect) to restore the user's saved language
export const restoreSavedLanguage = async (): Promise<void> => {
  const savedLang = getSavedLanguage();
  if (savedLang !== 'en') {
    await loadLanguage(savedLang);
    await i18n.changeLanguage(savedLang);
  }
};

/**
 * Extract language from a URL path and set i18n to that language SYNCHRONOUSLY.
 * Used during SSG and client hydration after resources have been loaded.
 *
 * IMPORTANT: call loadLanguage(lang) first to ensure resources are available.
 */
export const setLanguageFromPath = (pathname: string): string => {
  const lang = urlLangOf(pathname);

  // Synchronous language switch — sets internal state directly.
  i18n.language = lang;
  i18n.languages = [lang, 'en'];
  i18n.resolvedLanguage = lang;

  i18n.emit('languageChanged', lang);

  return lang;
};

export default i18n;
